import React, { useCallback, useContext } from 'react'

import NameTripModalView from './Templates/NameTripModalView'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FirstTime from 'tripPlanner/components/EntryPoints/Common/Copy'
import TripSelect from 'tripPlanner/components/EntryPoints/ModalChildren/TripSelect'
import TripContext, { TripContextValue } from 'tripPlanner/contexts/TripContext'
import { useCreateTripItem, useEditableTrips } from 'tripPlanner/hooks/api'
import { useProcessBookmarks } from 'tripPlanner/hooks/api/bookmark'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import useSaveButtonOnboardingTooltipRules from 'tripPlanner/hooks/onboarding/useSaveButtonOnboardingTooltipRules'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { BasicTrip, FullTrip } from 'tripPlanner/types/common'
import { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import { TripItem } from 'tripPlanner/types/tripItem'
import {
  bookmarkPayloadMappers,
  customItemPayloadMapper,
  isLeBookmark,
} from 'tripPlanner/utils'

interface Props {
  item: TripItem
  trip: FullTrip | PublicFullTrip
}

export default function SelectTripModalView({ item, trip }: Props) {
  const context = useContext<TripContextValue | null>(TripContext)

  const { closeModal, goBack } = useModalCallbacks()
  const { data: trips } = useEditableTrips()
  const openModal = useOpenModal()

  const { setUserHasSaved } = useSaveButtonOnboardingTooltipRules()

  const { showSaveErrorSnackbar, showSaveSuccessSnackbar } =
    useBookmarkSnackbarHandlers()

  const { mutate: processBookmarks, isLoading: isProcessingBookmarks } =
    useProcessBookmarks({
      onSuccess: (res, _, trip) => {
        setUserHasSaved()
        if (trip) {
          showSaveSuccessSnackbar(trip.id, trip.name, res.created[0].id)
        }
        closeModal()
      },
      onError: () => showSaveErrorSnackbar(trip.name),
    })

  const { mutate: createCustomItem, isLoading: isCreatingItems } =
    useCreateTripItem({
      onSuccess: (item, _, trip) => {
        setUserHasSaved()
        if (trip) {
          showSaveSuccessSnackbar(item.tripId, trip.name, item.id)
        }
        closeModal()
      },
      onError: () => showSaveErrorSnackbar(trip.name),
    })

  const saveItemToTrip = useCallback(
    (trip: BasicTrip) => {
      if (isLeBookmark(item)) {
        processBookmarks({
          items: [bookmarkPayloadMappers(item, context?.isPublicTrip)],
          tripIdsAdded: [trip.id],
          tripIdsRemoved: [],
        })
      } else {
        createCustomItem({
          tripId: trip.id,
          tripItem: customItemPayloadMapper(item, context?.isPublicTrip),
        })
      }
    },
    [item, processBookmarks, context?.isPublicTrip, createCustomItem],
  )

  const openTripNameView = useCallback(
    () => openModal(NameTripModalView, { trip, item }),
    [item, trip, openModal],
  )

  const isLoading = isCreatingItems || isProcessingBookmarks

  return (
    <>
      <ModalHeader
        title="Select a trip"
        onBackButtonClick={goBack}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="large">
              Saving this order does not hold the booking. Price and
              availability are subject to change.
            </BodyText>
            {trips && trips.length === 0 && <FirstTime />}
            {trips && (
              <div>
                <TripSelect
                  onCreateSelect={openTripNameView}
                  onSelectTrip={saveItemToTrip}
                  trips={trips}
                />
              </div>
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}
